import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Modal,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  getHolidayList,
  getReimbursementBills,
  getReimbursementCategories,
  getReimbursementCategoryData,
} from "_controllers/officer";
import authAxios from "setups/axios";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Toaster } from "react-hot-toast";
import moment from "moment";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { DeleteConfirmationModel } from "utils/helperComponent";
import { toastMessage } from "utils/toastMessage";
import Chart from "react-apexcharts";
import { PageHeader, SectionCard, StatCard } from "_shared/ui";
import { useChartBase, mergeChartOptions } from "_shared/ui/chartTheme";
import ViewBills from "./ViewBills";
import ExpenseBreakup from "./ExpenseBreakup";

const Main = () => {
  const [data, setData] = useState([]);
  const [billsData, setBillsData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletedItemId, setDeletedItemId] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [viewBillData, setViewBillData] = useState();

  const [isBreakupOpen, setIsBreakupOpen] = useState(false);

  const [addEdit, setAddEdit] = useState("Add");
  const [editId, seteditId] = useState("");

  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState([]);

  const [schemeErr, setschemeErr] = useState({
    category: false,
    subCategory: false,
  });

  const chartBase = useChartBase();

  const Validate = () => {
    let value = true;
    let err = {};

    if (category === "" || category === " ") {
      err.category = "Please Enter Category Name.";
      value = false;
    }

    if (subCategory.length === 0) {
      err.subCategory = "Please Enter Sub Category";
      value = false;
    }
    setschemeErr({ ...err });
    return value;
  };

  useEffect(async () => {
    const categoryData = await getReimbursementCategories();
    setData(categoryData?.data);
    setMessage(categoryData?.message);
    setLoading(true);
    setTotalPages(categoryData?.totalPage ?? 1);
  }, []);

  useEffect(async () => {
    const bills = await getReimbursementBills();
    setBillsData(bills?.data);
    setMessage(bills?.message);
    setLoading(true);
  }, []);

  useEffect(async () => {
    const category = await getReimbursementCategoryData();
    setCategoryData(category?.data);
    setMessage(category?.message);
    setLoading(true);
  }, []);

  const handleKeyPress = (e) => {
    const allowedCharacters = /^[a-zA-Z\s]+$/;
    if (!allowedCharacters.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleSelectedItemsChange = (event, newValue) => {
    setSubCategory(newValue);
  };

  const handleAddItem = (event) => {
    if (event.key === "Enter" && event.target.value !== "") {
      setSubCategory((prevItems) => [...prevItems, event.target.value]);
      event.target.value = "";
    }
  };

  const handleOpenModal = (data) => {
    setViewBillData(data);
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setViewBillData();
    setIsOpen(false);
  };

  const handleOpenBreakupModal = (data) => {
    setViewBillData(data);
    setIsBreakupOpen(true);
  };

  const handleBreakupClose = () => {
    setViewBillData();
    setIsBreakupOpen(false);
  };

  let topCategories = ["1", "2", "3", "4"];

  console.log("cate", categoryData);

  const totalBills = billsData?.reduce(
    (acc, crr) => acc + crr?.totalAmount,
    0
  );
  const pendingAmount =
    billsData.find((item) => item._id === "Pending")?.totalAmount || 0;
  const approvedAmount =
    billsData.find((item) => item._id === "Approved")?.totalAmount || 0;
  const rejectedAmount =
    billsData.find((item) => item._id === "Rejected")?.totalAmount || 0;

  // Donut: spend distribution (illustrative regional split).
  const donutOptions = mergeChartOptions(chartBase, {
    labels: ["North", "East", "South-West", "South", "West"],
    legend: { position: "bottom" },
  });

  // Grouped bar: this month vs last month.
  const monthCompareOptions = mergeChartOptions(chartBase, {
    labels: ["Phone", "Food", "Travel", "Gas", "Parking"],
    title: { text: "This Month vs Last Month", align: "left" },
    stroke: { show: true, width: 3, colors: ["transparent"] },
    tooltip: { followCursor: true },
    legend: { position: "top" },
  });

  // Bar: spend by categories (real category data).
  const categoryBarOptions = mergeChartOptions(chartBase, {
    labels: categoryData
      ?.sort((a, b) => a?._id.localeCompare(b?._id))
      ?.map((a) => a?._id),
    title: { text: "Spend By Categories", align: "left" },
    stroke: { show: true, width: 3, colors: ["transparent"] },
    tooltip: { followCursor: true },
    legend: { position: "top" },
  });

  return (
    <>
      <PageHeader
        title="Expense Overview"
        subtitle="Reimbursement summary and spend analytics"
      />

      {/* Reimbursement KPI cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Stack spacing={1} sx={{ height: "100%" }}>
            <StatCard
              label="All Bills"
              value={`₹ ${(totalBills ?? 0).toLocaleString()}`}
              icon={ReceiptLongIcon}
              color="primary"
            />
            <Button
              size="small"
              onClick={() => handleOpenBreakupModal(billsData)}
            >
              See Breakup
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Stack spacing={1} sx={{ height: "100%" }}>
            <StatCard
              label="Pending Bills"
              value={`₹ ${pendingAmount.toLocaleString()}`}
              icon={PendingActionsIcon}
              color="warning"
            />
            <Button
              size="small"
              onClick={() =>
                handleOpenModal(billsData.find((item) => item._id === "Pending"))
              }
            >
              View Bills
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Stack spacing={1} sx={{ height: "100%" }}>
            <StatCard
              label="Approved Bills"
              value={`₹ ${approvedAmount.toLocaleString()}`}
              icon={CheckCircleOutlineIcon}
              color="success"
            />
            <Button
              size="small"
              onClick={() =>
                handleOpenModal(
                  billsData.find((item) => item._id === "Approved")
                )
              }
            >
              View Bills
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Stack spacing={1} sx={{ height: "100%" }}>
            <StatCard
              label="Rejected Bills"
              value={`₹ ${rejectedAmount.toLocaleString()}`}
              icon={CancelOutlinedIcon}
              color="error"
            />
            <Button
              size="small"
              onClick={() =>
                handleOpenModal(
                  billsData.find((item) => item._id === "Rejected")
                )
              }
            >
              View Bills
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Category highlights + distribution */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Reimbursement Categories" sx={{ height: "100%" }}>
            <Grid container spacing={2}>
              {categoryData &&
                categoryData
                  .sort((a, b) => b.totalAmount - a.totalAmount)
                  .slice(0, 4)
                  .map((t) => (
                    <Grid item xs={6} key={t?._id}>
                      <Box
                        sx={{
                          height: "100%",
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          border: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                          bgcolor: "background.default",
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            fontWeight: 700,
                          }}
                        >
                          {t?._id.slice(0, 1)}
                        </Avatar>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {t?._id}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                          ₹ {t?.totalAmount.toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
            </Grid>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard title="Spend Distribution" sx={{ height: "100%" }}>
            <Box sx={{ height: 300 }}>
              <Chart
                type="donut"
                width={"100%"}
                height={"100%"}
                series={[100, 75, 150, 80, 120]}
                options={donutOptions}
              />
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Trend charts */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <SectionCard title="This Month vs Last Month">
            <Box sx={{ height: 280 }}>
              <Chart
                type="bar"
                width={"100%"}
                height={"100%"}
                series={[
                  {
                    name: "Last Month",
                    data: [100, 220, 150, 175, 300],
                  },
                  {
                    name: "Current Month",
                    data: [0, 100, 150, 250, 125],
                  },
                ]}
                options={monthCompareOptions}
              />
            </Box>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard title="Spend By Categories">
            <Box sx={{ height: 280 }}>
              <Chart
                type="bar"
                width={"100%"}
                height={"100%"}
                series={[
                  {
                    name: "Last Month",
                    data: categoryData
                      ?.sort((a, b) => a?._id.localeCompare(b?._id))
                      ?.map((a) => a?.totalAmount),
                  },
                ]}
                options={categoryBarOptions}
              />
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      <ViewBills
        isOpen={isOpen}
        handleClose={handleCloseModal}
        data={viewBillData}
      />
      {isBreakupOpen ? (
        <ExpenseBreakup
          isBreakupOpen={isBreakupOpen}
          handleBreakupClose={handleBreakupClose}
          data={viewBillData}
        />
      ) : null}
    </>
  );
};

const ExpenseDashboard = () => {
  return (
    <AccessController roles={["RO", "ZO", "ADMIN"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default ExpenseDashboard;
